import type { ApolloError } from '@apollo/client';
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Bid,
  Lot,
  LotDocument,
  LotQuery,
  useCallOutBidMutation,
  useMeQuery,
} from '../../graphql/types';
import { device } from '../../styles/devices';
import { color, gutter } from '../../styles/mixins';
import { label16, m20, p14 } from '../../styles/typography';
import {
  trackBidError,
  trackBidPlaced,
  trackBidStarted,
  trackBidStepCompleted,
} from '../../utils/analytics';
import { client, ws } from '../../utils/apollo-client';
import { useLotTimer } from '../../utils/use-lot-timer';
import { useNow } from '../../utils/use-now';
import { Alert, AlertVariant } from '../alert/alert';
import { Button } from '../button/button';
import { ConfirmBidModal } from '../confirm-bid-modal/confirm-bid-modal';
import { FormatAmount } from '../format-amount/format-amount';
import { LabelInput } from '../input/label-input';
import { SrOnly } from '../sr-only/sr-only';
import MinusSvg from '../svg/minus';
import PlusSvg from '../svg/plus';

// #region Styles
const Sticky = styled(motion.div)`
  position: fixed;
  top: 0;
  bottom: 0;
  z-index: 11;
  left: 0;
  right: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: scroll;
  min-height: 100vh;
  min-height: -webkit-fill-available;
`;

const BottomSheet = styled(motion.div)`
  display: flex;
  align-items: flex-end;
`;

const Form = styled.form`
  width: 100%;
  margin: 0 auto;
  padding: ${gutter(2)} ${gutter(2)};
  background-color: ${color('gray10')};
  max-width: ${({ theme }) =>
    theme.pageWidth + theme.breakpoints.sm.gutter * 2}px;

  @media ${device.mobile} {
    padding-left: ${({ theme }) => theme.breakpoints.sm.gutter}px;
    padding-right: ${({ theme }) => theme.breakpoints.sm.gutter}px;
  }

  @media ${device.tablet} {
    padding-left: ${({ theme }) => theme.breakpoints.md.gutter}px;
    padding-right: ${({ theme }) => theme.breakpoints.md.gutter}px;
    max-width: ${({ theme }) =>
      theme.pageWidth + theme.breakpoints.md.gutter * 2}px;
    max-width: ${({ theme }) =>
      theme.pageWidth + theme.breakpoints.md.gutter * 2}px;
  }

  @media ${device.desktop} {
    padding-left: ${({ theme }) => theme.breakpoints.lg.gutter}px;
    padding-right: ${({ theme }) => theme.breakpoints.lg.gutter}px;
    max-width: ${({ theme }) =>
      theme.pageWidth + theme.breakpoints.lg.gutter * 2}px;
  }
`;

const Toolbar = styled.div`
  position: relative;
`;

const Title = styled.div`
  ${m20};
  margin-bottom: ${gutter(1.5)};
`;

const CurrentBid = styled.div`
  ${label16};

  display: flex;
  justify-content: space-between;
  margin-bottom: ${gutter(2)};
`;

const Span = styled.span``;

const Content = styled.div`
  /* min-height: 180px; */
`;

const Offer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  margin-bottom: ${gutter(2)};
`;

const OfferBtn = styled(Button)`
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 54px;
  flex-shrink: 0;
`;

const InputWrap = styled.div`
  width: 100%;
  margin: 0 ${gutter(1)};
`;
const Shortcuts = styled.div`
  display: flex;
  flex-flow: row nowrap;
  margin-bottom: ${gutter(2)};
  overflow: scroll;
  width: calc(100% + 32px);
  margin-left: -${gutter(2)};
  padding-left: ${gutter(2)};

  @media ${device.mobile} {
    padding-left: ${({ theme }) => theme.breakpoints.sm.gutter}px;
    margin-left: ${({ theme }) => -theme.breakpoints.sm.gutter}px;
    width: ${({ theme }) =>
      `calc(100% + ${theme.breakpoints.sm.gutter * 2}px)`};
  }

  @media ${device.tablet} {
    padding-left: ${({ theme }) => theme.breakpoints.md.gutter}px;
    margin-left: ${({ theme }) => -theme.breakpoints.md.gutter}px;
    width: ${({ theme }) =>
      `calc(100% + ${theme.breakpoints.md.gutter * 2}px)`};
  }

  @media ${device.desktop} {
    padding-left: ${({ theme }) => theme.breakpoints.lg.gutter}px;
    margin-left: ${({ theme }) => -theme.breakpoints.lg.gutter}px;
    width: ${({ theme }) =>
      `calc(100% + ${theme.breakpoints.lg.gutter * 2}px)`};
  }

  > * {
    flex-shrink: 0;
    margin-right: ${gutter(1.5)};
  }
`;
const Info = styled.div`
  color: ${color('gray80')};
  margin-bottom: ${gutter(2)};
`;

const Message = styled.p`
  ${p14};
  margin-bottom: ${gutter(2)};
  strong {
    font-weight: 500;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-flow: column nowrap;
  > * {
    margin-bottom: ${gutter(1)};
  }
`;
// #endregion

const ONE_HOUR = 60 * 60 * 1000;

type BidSheetLot = Pick<
  Lot,
  | 'id'
  | 'number'
  | 'minimumIncrement'
  | 'maximumIncrement'
  | 'incrementAmount'
  | 'minimumBidAmount'
  | 'endsAt'
> & { highestBids?: Partial<Bid>[] | null };

export const BidSheet = ({ lot }: { lot?: LotQuery['Lot'] }) => {
  const navigate = useNavigate();
  const { isEnded, isStarted } = useLotTimer({
    startsAt: lot?.startsAt,
    endsAt: lot?.endsAt,
  });
  const me = useMeQuery({ fetchPolicy: 'cache-only' });

  const {
    maximumIncrement = 10,
    incrementAmount = 50,
    minimumBidAmount: startBidAmount = 0,
    highestBids,
    number,
  } = lot || ({} as BidSheetLot);

  const myLeaderboardBids = (lot?.leaderboard || [])
    .slice(0, 10)
    .filter((rank) => rank.paddleId === me.data?.me?.paddle?.id);

  const [highestBid] = highestBids || [];

  const highestBidAmount = highestBid?.amount;
  const minimumBidAmount = highestBidAmount
    ? highestBidAmount + incrementAmount
    : startBidAmount;
  const now = useNow();
  const endsAt = Date.parse(lot?.endsAt);
  const restricted =
    !lot?.endsAt || (lot?.endsAt && endsAt >= now && endsAt - now > ONE_HOUR);

  const [error, setError] = useState<ApolloError>();
  const [value, setValue] = useState(minimumBidAmount);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Get suggested bids
  const suggestedBids = Array.from({
    length: maximumIncrement,
  }).map((_, i) => minimumBidAmount + i * incrementAmount);

  const minimumAmount = suggestedBids[0];
  const maximumAmount = suggestedBids[suggestedBids.length - 1];

  useEffect(() => {
    trackBidStarted();
  }, []);

  const updateValue = (value: number) =>
    setValue(
      Math.max(
        !restricted ? value : Math.min(value, maximumAmount),
        minimumAmount
      )
    );

  useEffect(() => {
    trackBidStepCompleted({ amount: value });
  }, [value]);

  const [callOutBid, result] = useCallOutBidMutation();

  const onSubmitBid = (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    if (result?.loading) {
      return false;
    }
    setError(undefined);
    trackBidPlaced({ amount: value });
    callOutBid({
      variables: {
        lotId: lot?.id || '',
        amount: value,
        nvvip: localStorage.getItem('nvvip') === 'true',
      },
      onCompleted() {
        if (!ws.connected) {
          client.refetchQueries({ include: [LotDocument] });
        }
      },
      onError(err) {
        trackBidError({ error: err });
        client.refetchQueries({ include: [LotDocument] });
        setError(err);
      },
    });
    return false;
  };

  const setAmountAndSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    const amount = Number(e.currentTarget.dataset.amount);
    trackBidPlaced({ amount });
    callOutBid({
      variables: {
        lotId: lot?.id || '',
        amount,
        nvvip: localStorage.getItem('nvvip') === 'true',
      },
      onCompleted() {
        if (!ws.connected) {
          client.refetchQueries({ include: [LotDocument] });
        }
      },
      onError(err) {
        trackBidError({ error: err });
        client.refetchQueries({
          include: [LotDocument],
        });
        setError(err);
      },
    }).then(() => {
      setError(undefined);
    });
  };

  const buyersPremiumPercentage =
    me.data?.activeAuction?.buyersPremiumPercentage ?? 0;
  const overheadPremiumPercentage =
    me.data?.activeAuction?.overheadPremiumPercentage ?? 0;

  if (!lot) {
    return null;
  }

  if (isEnded || (!isStarted && localStorage?.getItem('nvvip') !== 'true')) {
    window.history.pushState(
      {},
      document.title,
      window.location.pathname.replace(/\/bid$/, '')
    );
    return null;
  }

  const info = `You can bid ${
    restricted
      ? `${minimumAmount.toLocaleString(
          'en-US'
        )} - ${maximumAmount.toLocaleString('en-US')} USD`
      : ''
  } in $${incrementAmount.toLocaleString(
    'en-US'
  )} increments. Note that all top 10 bids on the leaderboard win.`;

  const isSuccess = result.data?.callOutBid;
  let title = `Bidding on lot ${number}`;
  let message: React.ReactNode | string | undefined;
  let notification: string | undefined;
  let ctaBidAmount: number | undefined;

  if (error) {
    const [errorCode] = error.message.match(/E-(\d+)$/) || [];
    const errorMessage = error.message.replace(/ E-\d+$/, '');
    message = errorMessage;
    if (errorCode === 'E-443') {
      ctaBidAmount = maximumAmount;
    } else if (
      errorCode === 'E-442' ||
      errorMessage.startsWith('duplicate key value')
    ) {
      ctaBidAmount = minimumAmount;
      message = (
        <>
          Your bid of{' '}
          <strong>
            <FormatAmount value={Number(value)} />
          </strong>{' '}
          is no longer valid because the current highest bid has increased to{' '}
          <strong>
            <FormatAmount value={highestBidAmount ?? minimumBidAmount} />
          </strong>
          . You can choose to bid{' '}
          <strong>
            <FormatAmount value={minimumAmount} />
          </strong>{' '}
          or to cancel.
        </>
      );
    }
    notification = `Your bid was unsuccessful: ${errorMessage}`;
    title = 'Bid was unsuccessful';
  } else if (isSuccess) {
    title = 'Success';
    notification = `You successfully bid on lot ${number}`;
    message = (
      <>
        You currently have <strong>{myLeaderboardBids.length}</strong> winning
        bids of{' '}
        {myLeaderboardBids.map((bid, i, all) => (
          <React.Fragment key={i}>
            <strong>
              <FormatAmount value={bid.amount} />
            </strong>
            {i === all.length - 2 ? ' and ' : i < all.length - 1 ? ', ' : ''}
          </React.Fragment>
        ))}{' '}
        (for {myLeaderboardBids.length} cases). We will notify you when you win,
        or are overbid and drop off the leaderboard.
      </>
    );
  }

  let alert;
  if (!restricted) {
    alert = (
      <Alert
        copy={
          <>
            <strong>&lt; 1 hour remaining:</strong> Upper limits on bids have
            been lifted. You are free to bid any amount over the highest bid.
          </>
        }
        variant={AlertVariant.Warning}
      />
    );
  }

  if (isSuccess || error) {
    alert = (
      <Alert
        copy={notification}
        variant={error ? AlertVariant.Danger : AlertVariant.Success}
      />
    );
  }

  return (
    <Sticky
      initial={{ backgroundColor: 'rgba(255, 255, 255, 0)' }}
      animate={{ backgroundColor: 'rgba(255, 255, 255, 0.8)' }}
      exit={{ backgroundColor: 'rgba(255, 255, 255, 0)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          navigate(window.location.pathname.replace(/\/bid$/, ''));
        }
      }}
    >
      <div>{alert ? <Toolbar>{alert}</Toolbar> : null}</div>
      <BottomSheet
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0, transition: { duration: 0.18 } }}
        exit={{ opacity: 0, y: 32, transition: { duration: 0.18 } }}
      >
        {/* <Container> */}
        <Form onSubmit={onSubmitBid}>
          <Title>{title}</Title>
          <CurrentBid>
            {highestBidAmount ? (
              <>
                <Span>Current highest bid </Span>
                <Span>
                  <FormatAmount value={highestBidAmount} />
                </Span>
              </>
            ) : (
              <>
                <Span>Minimum bid </Span>
                <Span>
                  <FormatAmount value={minimumBidAmount} />
                </Span>
              </>
            )}
          </CurrentBid>
          <Content>
            {!message ? (
              <>
                <Offer>
                  <OfferBtn
                    onClick={() => updateValue(value - incrementAmount)}
                    disabled={value <= minimumAmount}
                    type="button"
                  >
                    <MinusSvg />
                    <SrOnly>Reduce bid</SrOnly>
                  </OfferBtn>
                  <InputWrap>
                    <LabelInput
                      id="offer"
                      label="Your offer"
                      value={value}
                      onValueChange={(values) => {
                        const { value } = values;
                        setValue(Number(value));
                      }}
                    />
                  </InputWrap>
                  <OfferBtn
                    onClick={() => updateValue(value + incrementAmount)}
                    disabled={restricted && value >= maximumAmount}
                    type="button"
                  >
                    <PlusSvg />
                    <SrOnly>Raise bid</SrOnly>
                  </OfferBtn>
                </Offer>
                {restricted && (
                  <Shortcuts>
                    {suggestedBids.map((suggestedBid) => (
                      <Button
                        type="button"
                        onClick={() => setValue(suggestedBid)}
                        size="sm"
                        key={suggestedBid}
                        variant={
                          suggestedBid === value ? 'primary' : 'secondary'
                        }
                      >
                        <FormatAmount value={suggestedBid} />
                      </Button>
                    ))}
                  </Shortcuts>
                )}
                <Info>{info}</Info>
              </>
            ) : (
              <Message>{message}</Message>
            )}
          </Content>
          <Actions>
            {isSuccess ? (
              <Button as={Link} to="/my-lots">
                Go to My Lots
              </Button>
            ) : ctaBidAmount ? (
              <Button
                type="button"
                data-amount={ctaBidAmount}
                onClick={setAmountAndSubmit}
                disabled={result?.loading}
              >
                Bid <FormatAmount value={ctaBidAmount} />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={() => setShowConfirmModal(true)}
                disabled={result?.loading}
              >
                Submit bid
              </Button>
            )}
            <Button
              variant="secondary"
              as={Link}
              to={window.location.pathname.replace(/\/bid$/, '')}
            >
              {isSuccess ? 'Close' : 'Cancel'}
            </Button>
          </Actions>
          {showConfirmModal && !isSuccess && !error && (
            <ConfirmBidModal
              bidAmount={value}
              onClose={() => setShowConfirmModal(false)}
              disabled={result?.loading}
              buyersPremiumPercentage={buyersPremiumPercentage}
              overheadPremiumPercentage={overheadPremiumPercentage}
            />
          )}
        </Form>
        {/* </Container> */}
      </BottomSheet>
    </Sticky>
  );
};
